import { createContext, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchCustomerCart, fetchDeletedProductIds, saveCustomerCart } from '../lib/firebase';
import { trackAddToCart } from '../lib/analytics';
import { normalizeProductPricing } from '../lib/productPricing';
import { isProductUnavailable } from '../lib/productAvailability';
import toast from 'react-hot-toast';
import { useCustomerAuth } from './CustomerAuthContext';

const CartContext = createContext();

const isSameCartItem = (left, right) =>
  left.id === right.id &&
  (left.size || null) === (right.size || null) &&
  (left.color || null) === (right.color || null);

const mergeCartItems = (baseItems = [], incomingItems = []) => {
  const merged = [...baseItems];

  incomingItems.forEach((incoming) => {
    const existingIndex = merged.findIndex((item) => isSameCartItem(item, incoming));

    if (existingIndex >= 0) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...incoming,
        quantity: (merged[existingIndex].quantity || 0) + (incoming.quantity || 0),
      };
      return;
    }

    merged.push(incoming);
  });

  return merged;
};

const normalizeCartItem = (item = {}) => ({
  ...normalizeProductPricing(item),
  quantity: Math.max(1, Number(item?.quantity) || 1),
  size: item?.size || null,
  color: item?.color || null,
});

export const CartProvider = ({ children }) => {
  const { user, authReady } = useCustomerAuth();
  const [cart, setCart] = useState([]);
  const [cartReady, setCartReady] = useState(false);
  const skipNextRemoteSyncRef = useRef(false);

  useEffect(() => {
    if (!authReady) return;

    let isActive = true;

    const syncCustomerCart = async () => {
      let deletedProductIds = new Set();
      try {
        deletedProductIds = new Set(await fetchDeletedProductIds());
      } catch (error) {
        console.warn('Could not check deleted products in customer cart:', error);
      }

      const localCart = (JSON.parse(localStorage.getItem('cart') || '[]') || [])
        .filter((item) => !deletedProductIds.has(item?.id))
        .map((item) => normalizeCartItem(item));

      if (!user) {
        if (!isActive) return;
        setCart(localCart);
        setCartReady(true);
        return;
      }

      try {
        const remoteCart = await fetchCustomerCart(user.uid);
        const mergedCart = mergeCartItems(remoteCart, localCart)
          .filter((item) => !deletedProductIds.has(item?.id))
          .map((item) => normalizeCartItem(item));

        if (!isActive) return;

        skipNextRemoteSyncRef.current = true;
        setCart(mergedCart);
        localStorage.setItem('cart', JSON.stringify(mergedCart));
        await saveCustomerCart(user.uid, mergedCart);
      } catch (error) {
        console.warn('Could not sync customer cart:', error);
        if (isActive) {
          setCart(localCart);
        }
      } finally {
        if (isActive) {
          setCartReady(true);
        }
      }
    };

    syncCustomerCart();

    return () => {
      isActive = false;
    };
  }, [user, authReady]);

  useEffect(() => {
    if (!cartReady) return;

    localStorage.setItem('cart', JSON.stringify(cart));

    if (!user) return;
    if (skipNextRemoteSyncRef.current) {
      skipNextRemoteSyncRef.current = false;
      return;
    }

    saveCustomerCart(user.uid, cart).catch((error) => {
      console.warn('Could not save customer cart:', error);
    });
  }, [cart, user, cartReady]);

  const addToCart = (product, qty = 1, size = null, color = null) => {
    if (isProductUnavailable(product)) {
      toast.error('This product is currently unavailable.');
      return false;
    }

    trackAddToCart(product, qty);
    setCart((previous) => {
      const nextItem = normalizeCartItem({
        ...product,
        quantity: qty,
        size,
        color,
      });
      const existing = previous.find((item) => item.id === product.id && item.size === size && item.color === color);

      if (existing) {
        return previous.map((item) =>
          item.id === product.id && item.size === size && item.color === color
            ? { ...item, ...nextItem, quantity: item.quantity + qty }
            : item
        );
      }

      return [...previous, nextItem];
    });
    return true;
  };

  const removeFromCart = (id, size, color) => {
    setCart((previous) => previous.filter((item) => !(item.id === id && item.size === size && item.color === color)));
  };

  const updateQuantity = (id, size, color, qty) => {
    if (qty <= 0) {
      removeFromCart(id, size, color);
      return;
    }

    setCart((previous) =>
      previous.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity: qty }
          : item
      )
    );
  };

  const clearCart = () => setCart([]);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * item.quantity, 0);

  const value = {
    cart,
    cartReady,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

CartProvider.propTypes = {
  children: PropTypes.node,
};

export const useCart = () => useContext(CartContext);
